import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { adminListLegalPages, adminUpdateLegalPage, adminTranslate } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Sparkles, Save, Loader2, FileText, Shield, Cookie, Heart, Eye, EyeOff, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

const ICONS = {
  terms: FileText,
  privacy: Shield,
  cookies: Cookie,
  'responsible-play': Heart,
};

export default function AdminLegalPages() {
  const { user, isAdmin, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState('terms');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  const [previewLang, setPreviewLang] = useState('fr');

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadPages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const loadPages = async () => {
    try {
      const res = await adminListLegalPages();
      const list = res.data?.pages || [];
      setPages(list);
      // initialise active page draft
      const active = list.find((p) => p.slug === activeSlug) || list[0];
      if (active) {
        setActiveSlug(active.slug);
        setDraft({ ...active });
      }
    } catch {
      toast.error('Erreur de chargement');
    }
  };

  const handleSwitch = (slug) => {
    setActiveSlug(slug);
    const p = pages.find((x) => x.slug === slug);
    if (p) setDraft({ ...p });
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await adminUpdateLegalPage(draft.slug, {
        title_en: draft.title_en,
        title_fr: draft.title_fr,
        content_en: draft.content_en,
        content_fr: draft.content_fr,
        enabled: draft.enabled,
      });
      toast.success('Page sauvegardée');
      // refresh local list
      setPages((prev) => prev.map((p) => (p.slug === draft.slug ? { ...p, ...draft } : p)));
    } catch (e) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoTranslate = async (field, direction) => {
    // field: 'title' | 'content' ; direction: 'fr-to-en' | 'en-to-fr'
    const isFrToEn = direction === 'fr-to-en';
    const source = isFrToEn ? 'fr' : 'en';
    const target = isFrToEn ? 'en' : 'fr';
    const sourceField = `${field}_${source}`;
    const targetField = `${field}_${target}`;
    const text = draft?.[sourceField] || '';
    if (!text.trim()) {
      toast.error(`Champ ${sourceField} vide`);
      return;
    }
    const id = `${field}-${direction}`;
    setTranslating(id);
    try {
      const res = await adminTranslate(text, source, target, `Legal page (${draft.slug}) ${field}`);
      setDraft((prev) => ({ ...prev, [targetField]: res.data?.translation || '' }));
      toast.success(res.data?.cached ? 'Traduit (cache)' : `Traduit (${res.data?.tokens_used || '?'} tokens)`);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Erreur de traduction';
      if (status === 402) {
        toast.error('OpenAI: crédit épuisé. Vérifiez votre solde.');
      } else {
        toast.error(msg);
      }
    } finally {
      setTranslating(null);
    }
  };

  if (loading || !draft) return <div className="min-h-screen flex"><AdminSidebar /><div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-gold" /></div></div>;

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-rolex-green mb-1">Pages Légales</h1>
            <p className="text-rolex-green/65 text-sm">Gérez le contenu, la traduction et la visibilité des pages juridiques du site.</p>
          </div>

          {/* Page selector */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {pages.map((p) => {
              const Icon = ICONS[p.slug] || FileText;
              const active = p.slug === activeSlug;
              return (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => handleSwitch(p.slug)}
                  style={active ? { backgroundColor: 'hsl(var(--rolex-green))', color: 'hsl(43 38% 92%)' } : {}}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    active
                      ? 'border-gold shadow-lg scale-[1.02]'
                      : 'bg-white border-gold/20 hover:border-gold/40 text-rolex-green'
                  }`}
                  data-testid={`legal-tab-${p.slug}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`w-5 h-5 ${active ? 'text-gold' : 'text-gold-dark'}`} />
                    <Badge className={p.enabled ? 'bg-emerald-500/90 text-white border-none' : 'bg-stone-300 text-stone-700 border-none'}>
                      {p.enabled ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                      {p.enabled ? 'Actif' : 'Off'}
                    </Badge>
                  </div>
                  <p className="font-semibold text-sm" style={active ? { color: 'hsl(43 38% 92%)' } : { color: 'hsl(var(--rolex-green))' }}>
                    {p.title_fr || p.title_en}
                  </p>
                  <p className="text-[10px] uppercase tracking-wide mt-1" style={active ? { color: 'hsl(43 38% 80%)' } : { color: 'hsl(var(--rolex-green) / 0.55)' }}>
                    /{p.slug}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Editor card */}
          <Card className="bg-white border-2 border-gold/30 shadow-xl">
            <CardHeader className="border-b border-gold/20 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-rolex-green flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gold-dark" />
                  Édition : <span className="text-gold-dark">{draft.title_fr || draft.title_en}</span>
                </CardTitle>
                <p className="text-xs text-rolex-green/55 mt-1 font-mono">URL publique : /{draft.slug === 'responsible-play' ? 'responsible-play' : draft.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`/${draft.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gold-dark hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Voir page publique
                </a>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-rolex-green/65">Activer</Label>
                  <Switch
                    checked={draft.enabled !== false}
                    onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                    data-testid="legal-enabled-toggle"
                  />
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Title fields */}
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-rolex-green flex items-center gap-2">🇫🇷 Titre FR</Label>
                    <Button size="sm" variant="ghost" disabled={translating !== null || !draft.title_en?.trim()} onClick={() => handleAutoTranslate('title', 'en-to-fr')} className="text-xs h-7 text-gold-dark hover:bg-gold/10">
                      {translating === 'title-en-to-fr' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} EN→FR
                    </Button>
                  </div>
                  <Input value={draft.title_fr || ''} onChange={(e) => setDraft({ ...draft, title_fr: e.target.value })} className="border-gold/30" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-rolex-green flex items-center gap-2">🇬🇧 Title EN</Label>
                    <Button size="sm" variant="ghost" disabled={translating !== null || !draft.title_fr?.trim()} onClick={() => handleAutoTranslate('title', 'fr-to-en')} className="text-xs h-7 text-gold-dark hover:bg-gold/10">
                      {translating === 'title-fr-to-en' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} FR→EN
                    </Button>
                  </div>
                  <Input value={draft.title_en || ''} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} className="border-gold/30" />
                </div>
              </div>

              {/* Content tabs */}
              <Tabs defaultValue="fr">
                <TabsList className="grid grid-cols-2 max-w-sm bg-cream border border-gold/30">
                  <TabsTrigger value="fr" data-testid="content-tab-fr">🇫🇷 Contenu FR</TabsTrigger>
                  <TabsTrigger value="en" data-testid="content-tab-en">🇬🇧 Content EN</TabsTrigger>
                </TabsList>

                <TabsContent value="fr" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-rolex-green/70">Markdown supporté (## titres, **gras**, listes, liens…)</Label>
                    <Button size="sm" variant="outline" disabled={translating !== null || !draft.content_en?.trim()} onClick={() => handleAutoTranslate('content', 'en-to-fr')} className="border-gold/40 text-gold-dark hover:bg-gold/10">
                      {translating === 'content-en-to-fr' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Traduire EN → FR
                    </Button>
                  </div>
                  <Textarea
                    value={draft.content_fr || ''}
                    onChange={(e) => setDraft({ ...draft, content_fr: e.target.value })}
                    className="font-mono text-sm border-gold/30 min-h-[480px] bg-cream/50"
                    rows={20}
                    data-testid="content-fr-textarea"
                  />
                </TabsContent>

                <TabsContent value="en" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-rolex-green/70">Markdown supported (## headings, **bold**, lists, links…)</Label>
                    <Button size="sm" variant="outline" disabled={translating !== null || !draft.content_fr?.trim()} onClick={() => handleAutoTranslate('content', 'fr-to-en')} className="border-gold/40 text-gold-dark hover:bg-gold/10">
                      {translating === 'content-fr-to-en' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Translate FR → EN
                    </Button>
                  </div>
                  <Textarea
                    value={draft.content_en || ''}
                    onChange={(e) => setDraft({ ...draft, content_en: e.target.value })}
                    className="font-mono text-sm border-gold/30 min-h-[480px] bg-cream/50"
                    rows={20}
                    data-testid="content-en-textarea"
                  />
                </TabsContent>
              </Tabs>

              {/* Live preview */}
              <div className="border-2 border-dashed border-gold/30 rounded-xl p-5 bg-cream/40">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-rolex-green flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gold-dark" /> Aperçu live
                  </h3>
                  <div className="flex gap-1 text-xs">
                    <button onClick={() => setPreviewLang('fr')} className={`px-3 py-1 rounded ${previewLang === 'fr' ? 'bg-gold text-rolex-green font-bold' : 'bg-stone-100 text-stone-600'}`}>🇫🇷 FR</button>
                    <button onClick={() => setPreviewLang('en')} className={`px-3 py-1 rounded ${previewLang === 'en' ? 'bg-gold text-rolex-green font-bold' : 'bg-stone-100 text-stone-600'}`}>🇬🇧 EN</button>
                  </div>
                </div>
                <article className="prose prose-stone max-w-none prose-headings:text-rolex-green prose-h1:text-2xl prose-h2:text-xl prose-strong:text-rolex-green text-sm">
                  <h1 className="font-display">{previewLang === 'fr' ? draft.title_fr : draft.title_en}</h1>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewLang === 'fr' ? (draft.content_fr || '') : (draft.content_en || '')}</ReactMarkdown>
                </article>
              </div>

              <div className="flex justify-end pt-4 border-t border-gold/20">
                <Button onClick={handleSave} disabled={saving} className="btn-gold gap-2 px-8" data-testid="save-legal-page">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Sauvegarder
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
